import { zodResolver } from "@hookform/resolvers/zod";
import type { SetStateAction } from "react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@calcom/trpc/react";
import { Button, Dialog, DialogContent, Form, showToast, TextField } from "@calcom/ui";

type CreatedPatient = {
  patientId: number;
  firstName: string;
  lastName: string;
};

type Props = {
  open: boolean;
  handler: React.Dispatch<SetStateAction<boolean>>;
  officeId: number;
  onCreated?: (patient: CreatedPatient) => void;
};

export type FormValues = {
  firstName: string;
  lastName: string;
  homePhone: string;
  workPhone: string;
  cellPhone: string;
  email: string;
  address: string;
  birthday: string;
  lastVisitDate: string;
  balance: number;
  insuranceEstimate: number;
  pmsId: string;
};

export default function AddPatientDialog({ open, handler, officeId, onCreated }: Props) {
  const utils = trpc.useContext();

  const [loading, setLoading] = useState(false);
  const form = useForm<FormValues>({
    defaultValues: {
      balance: 0,
      insuranceEstimate: 0,
    },
    resolver: zodResolver(
      z.object({
        firstName: z.string().min(1),
        lastName: z.string().optional(),
        homePhone: z.string().optional(),
        workPhone: z.string().optional(),
        cellPhone: z.string().optional(),
        email: z.string().optional(),
        address: z.string().optional(),
        birthday: z.string().optional(),
        lastVisitDate: z.string().optional(),
        balance: z.number(),
        insuranceEstimate: z.number(),
        pmsId: z.string().optional(),
      })
    ),
  });

  const { register } = form;

  const addMutation = trpc.viewer.patients.addPatient.useMutation({
    onSuccess: (created) => {
      showToast("Successfully added", "success");
      utils.viewer.patients.invalidate();
      setLoading(false);
      form.reset({ balance: 0, insuranceEstimate: 0 });
      onCreated?.({
        patientId: created.patientId,
        firstName: created.firstName ?? "",
        lastName: created.lastName ?? "",
      });
      if (onCreated) handler(false);
    },
    onError: (error: any) => {
      showToast(error?.message || "Unknown error", "error");
      setLoading(false);
    },
  });

  const submitValues = (data: any) => {
    setLoading(true);
    addMutation.mutate({ ...data, officeId });
  };

  return (
    <Dialog open={open} onOpenChange={handler}>
      <DialogContent className="p-0">
        <p className="border-b px-8 pt-8 pb-4 text-base text-gray-600">Add Patient</p>
        <Form form={form} handleSubmit={submitValues}>
          <div className="grid grid-cols-2 gap-4 px-8">
            <TextField label="First Name*" {...register("firstName")} />
            <TextField label="Last Name*" {...register("lastName")} />
            <TextField label="Home Phone" {...register("homePhone")} />
            <TextField label="Work Phone" {...register("workPhone")} />
            <TextField label="Cell Phone" {...register("cellPhone")} />
            <TextField label="Email Address" {...register("email")} />
            <TextField label="Address" className="col-span-2" {...register("address")} />
            <TextField label="Birthday" {...register("birthday")} type="date" />
            <TextField label="Last Visit Date" {...register("lastVisitDate")} type="date" />
            <TextField
              label="Balance"
              {...register("balance", { valueAsNumber: true })}
              type="number"
              min={0}
              step={0.01}
            />
            <TextField
              label="Insurance Estimate"
              {...register("insuranceEstimate", { valueAsNumber: true })}
              type="number"
              min={0}
              step={0.01}
            />
            <TextField label="PMS ID" {...register("pmsId")} />
          </div>
          <div className="mt-4 flex justify-end gap-4 border-t px-8 py-4">
            <Button
              color="secondary"
              className="border bg-white text-red-500 hover:bg-white hover:text-red-500"
              onClick={() => handler(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              loading={loading}
              className="border  bg-white text-gray-600 hover:bg-white hover:text-gray-600">
              Add Account
            </Button>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
